import { List, Map, fromJS } from 'immutable';
import layerPanelConfig from 'config/layerPanel';

import debug from 'utils/debug';
import * as actions from './actions';
import * as datasetsActions from '../datasets/actions';
import * as geofencesActions from '../geofences/actions';
import * as commuteOfferActions from '../commuteOffer/actions';
import * as userActions from '../user/actions';

const { REDUX_EVENT_HANDLER } = debug('m:ui:index');

export const initialState = fromJS({
  commuteOffer: {
    isBookingsHidden: true,
    activeVehicleIds: [],
    activeRouteStopId: null,
    activeRouteStopUid: null,
    activeBookingId: null,
    editableBookingId: null,
    busStopsTypeVisible: [],
    editableVehicle: null,
    editableVehicleTime: false,
    searchQuery: '',
    bookingsFilter: window.GEODISC_DEBUG_DEFAULT_BOOKINGS_PANEL_FILTER || '',
    vehiclesFilter: window.GEODISC_DEBUG_DEFAULT_VEHICLES_PANEL_FILTER || '',
    routingEngine: window.GEODISC_ROUTING_ENGINE || 'euclidean',
    walkingRoutingEngine: window.GEODISC_WALKING_ROUTING_ENGINE || 'euclidean',
    draggablePoint: null,
    addPointMode: null,
    addStopMode: null,
    stopSearchQuery: '',
    isChanged: false,
    isSaving: false,
    selected: [],
    fetchError: false,
    ordering: {
      id: 'name',
      sort: false // true, false
    }
  },
  datasets: {
    ordering: {
      id: 'created_at',
      sort: false // true, false
    },
    selectedIds: [],
    search: '',
    fetchError: false
  },
  dataset: {
    editable: null,
    layerPanel: {
      // points, arcs, clusters, arcClusters, hexPickup, hexDropoff
      activeLayer: 'points',
      ...layerPanelConfig
    },
    time: 0,
    allDay: false,
    running: false,
    fetchError: false,
    fetchCsvError: false
  },
  geofences: {
    editable: null,
    hoverId: false,
    hidden: [],
    search: '',
    fetchError: false
  },
  simulations: {
    ordering: {
      id: 'created_at',
      sort: false // true, false
    },
    search: '',
    selected: [],
    compareMode: false,
    fetchError: false
  },
  newDataset: {
    progress: 0,
    status: null,
    error: null
  },
  importBookings: {
    progress: 0,
    status: null,
    error: null
  },
  progressWindow: {
    progress: 0,
    status: null
  },
  login: {
    waiting: false,
    error: null
  },
  modalWindow: null
});

const reducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case commuteOfferActions.COMMUTE_OFFER_STATELESS_NODE_SCHEDULER_SUCCESS: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'isChanged'], true);
    }
    case actions.MODAL_WINDOW_SET_PROGRESS: {
      REDUX_EVENT_HANDLER(type, payload);

      const { progress, status } = payload;

      return state
        .setIn(['progressWindow', 'progress'], progress)
        .setIn(['progressWindow', 'status'], status);
    }
    case actions.SEARCH_QUERY_CHANGED: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'searchQuery'], payload);
    }
    case actions.BOOKINGS_FILTER_CHANGED: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'bookingsFilter'], payload);
    }
    case actions.VEHICLES_FILTER_CHANGED: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'vehiclesFilter'], payload);
    }
    case actions.SET_IS_CHANGED: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'isChanged'], payload);
    }
    case commuteOfferActions.COMMUTE_OFFER_ADD_VEHICLE_SUCCESS: {
      REDUX_EVENT_HANDLER(type, payload);

      return state
        .setIn(['commuteOffer', 'isChanged'], payload)
        .set('modalWindow', null);
    }
    case commuteOfferActions.COMMUTE_OFFER_EDIT_VEHICLE_SUCCESS: {
      REDUX_EVENT_HANDLER(type, payload);

      return state
        .setIn(['commuteOffer', 'isChanged'], payload)
        .set('modalWindow', null);
    }
    case actions.SET_IS_SAVING: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'isSaving'], payload);
    }
    case actions.TOGGLE_BOOKINGS_HIDDEN: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.updateIn(
        ['commuteOffer', 'isBookingsHidden'],
        isBookingsHidden => !isBookingsHidden
      );
    }
    case actions.TOGGLE_EDITABLE_TIME_MODE: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.updateIn(
        ['commuteOffer', 'editableVehicleTime'],
        editableVehicleTime => !editableVehicleTime
      );
    }
    case actions.SET_ADD_STOP_MODE: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'addStopMode'], payload);
    }
    case actions.CLEAN_ADD_STOP_MODE: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'addStopMode'], null);
    }
    case actions.SET_STOP_SEARCH_QUERY: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'stopSearchQuery'], payload);
    }
    case actions.CLEAN_STOP_SEARCH_QUERY: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'stopSearchQuery'], '');
    }
    case actions.SET_DRAGGABLE_POINT: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.updateIn(
        ['commuteOffer', 'draggablePoint'],
        draggablePoint =>
          draggablePoint ? { ...draggablePoint, ...payload } : payload
      );
    }
    case actions.REMOVE_DRAGGABLE_POINT: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'draggablePoint'], null);
    }
    case actions.SET_ACTIVE_VEHICLE_ID: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;
      const index = state
        .getIn(['commuteOffer', 'activeVehicleIds'])
        .findIndex(item => item === id);

      if (index !== -1) {
        return state;
      }

      return state.updateIn(
        ['commuteOffer', 'activeVehicleIds'],
        activeVehicleIds => activeVehicleIds.push(id)
      );
    }
    case actions.CLEAN_ACTIVE_VEHICLE_ID: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;
      const index = state
        .getIn(['commuteOffer', 'activeVehicleIds'])
        .findIndex(item => item === id);

      return state.updateIn(
        ['commuteOffer', 'activeVehicleIds'],
        activeVehicleIds => activeVehicleIds.delete(index)
      );
    }
    case actions.CLEAN_ALL_ACTIVE_VEHICLE_IDS: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'activeVehicleIds'], List);
    }
    case actions.SET_ACTIVE_BOOKING_ID: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;
      return state.setIn(['commuteOffer', 'activeBookingId'], id);
    }
    case actions.CLEAN_ACTIVE_BOOKING_ID: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'activeBookingId'], null);
    }
    case actions.SET_EDITABLE_BOOKING_ID: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;
      return state.setIn(['commuteOffer', 'editableBookingId'], id);
    }
    case actions.CLEAN_EDITABLE_BOOKING_ID: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'editableBookingId'], null);
    }
    case actions.SET_ACTIVE_ROUTE_STOP: {
      REDUX_EVENT_HANDLER(type, payload);

      const { id, uid } = payload;
      return state
        .setIn(['commuteOffer', 'activeRouteStopId'], id)
        .setIn(['commuteOffer', 'activeRouteStopUid'], uid);
    }
    case actions.CLEAN_ACTIVE_ROUTE_STOP: {
      REDUX_EVENT_HANDLER(type, payload);

      return state
        .setIn(['commuteOffer', 'activeRouteStopId'], null)
        .setIn(['commuteOffer', 'activeRouteStopUid'], null);
    }
    case actions.SET_EDITABLE_VEHICLE_ID: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;
      return state.setIn(['commuteOffer', 'editableVehicleId'], id);
    }
    case actions.CLEAR_EDITABLE_VEHICLE_ID: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'editableVehicleId'], null);
    }
    case actions.TOGGLE_ACTIVE_STOPS_TYPE: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;
      return state.updateIn(
        ['commuteOffer', 'busStopsTypeVisible'],
        (busStopsTypeVisible) => {
          const index = busStopsTypeVisible.findIndex(item => item === id);

          if (index !== -1) {
            return busStopsTypeVisible
              .slice(0, index)
              .concat(
                busStopsTypeVisible.slice(index + 1, busStopsTypeVisible.length)
              );
          }

          return [...busStopsTypeVisible, id];
        }
      );
    }
    case actions.CLEAR_ACTIVE_STOPS_TYPE: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'busStopsTypeVisible'], []);
    }
    case actions.TOGGLE_ROUTING_ENGINE: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;
      return state.setIn(['commuteOffer', 'routingEngine'], id);
    }
    case actions.TOGGLE_WALKING_ROUTING_ENGINE: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;
      global.GEODISC_WALKING_ROUTING_ENGINE = id;
      global.WALKING_ROUTES = {};
      global.WALKING_ROUTES_BY_IDS = {};
      return state.setIn(['commuteOffer', 'walkingRoutingEngine'], id);
    }
    case actions.SET_ADD_POINT_MODE: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'addPointMode'], payload);
    }
    case actions.CLEAN_ADD_POINT_MODE: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['commuteOffer', 'addPointMode'], null);
    }
    case actions.CLEAN_COMMUTE_OFFER_UI: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.set('commuteOffer', initialState.get('commuteOffer'));
    }
    // datasets
    case actions.CHANGE_DATASETS_ORDERING: {
      REDUX_EVENT_HANDLER(type, payload);

      const newId = payload;

      const id = state.getIn(['datasets', 'ordering', 'id']);

      if (id === newId) {
        return state.updateIn(
          ['datasets', 'ordering', 'sort'],
          sortOrdering => !sortOrdering
        );
      }

      return state.setIn(
        ['datasets', 'ordering'],
        Map({
          id: newId,
          sort: false
        })
      );
    }
    case actions.CHANGE_DATASETS_SEARCH: {
      REDUX_EVENT_HANDLER(type, payload);

      const text = payload;

      return state.setIn(['datasets', 'search'], text);
    }
    case datasetsActions.DATASETS_FETCH_FAILURE: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['datasets', 'fetchError'], true);
    }
    case actions.TOGGLE_SELECTED_DATASET: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;
      return state.updateIn(['datasets', 'selectedIds'], (selected) => {
        const index = selected.indexOf(id);
        if (index === -1) {
          return selected.push(id);
        }

        return selected.delete(index);
      });
    }
    case actions.CLEAR_SELECTED_DATASETS: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['datasets', 'selectedIds'], List());
    }
    // dataset
    case actions.LAYER_PANEL_CHANGE: {
      REDUX_EVENT_HANDLER(type, payload);

      const { paramId, value } = payload;
      const layerId = state.getIn(['dataset', 'layerPanel', 'activeLayer']);
      return state.setIn(
        ['dataset', 'layerPanel', layerId, paramId],
        fromJS(value)
      );
    }
    case actions.LAYER_TOGGLE: {
      REDUX_EVENT_HANDLER(type, payload);

      const layerId = payload;
      return state.setIn(['dataset', 'layerPanel', 'activeLayer'], layerId);
    }
    case actions.SET_EDITABLE_DATASET: {
      REDUX_EVENT_HANDLER(type, payload);

      const dataset = payload;

      return state.setIn(['dataset', 'editable'], dataset);
    }
    case actions.UPDATE_EDITABLE_DATASET: {
      REDUX_EVENT_HANDLER(type, payload);

      const { property, value } = payload;

      return state.setIn(['dataset', 'editable', property], fromJS(value));
    }
    case actions.CHANGE_DATASET_TIME: {
      REDUX_EVENT_HANDLER(type, payload);

      const time = payload;

      return state.setIn(['dataset', 'time'], parseInt(time, 10));
    }
    case datasetsActions.CLEAR_DATASET: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['dataset', 'time'], 0);
    }
    case datasetsActions.DATASET_FETCH_FAILURE: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['dataset', 'fetchError'], true);
    }
    case datasetsActions.CSV_FETCH_FAILURE: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['dataset', 'fetchCsvError'], true);
    }
    case actions.DATASET_TOGGLE_ALLDAY: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.updateIn(['dataset', 'allDay'], allDay => !allDay);
    }
    case actions.DATASET_PLAY: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['dataset', 'running'], true);
    }
    case actions.DATASET_STOP: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['dataset', 'running'], false);
    }
    case actions.DATASET_NEXT: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.updateIn(['dataset', 'time'], time =>
        time >= 23 ? 0 : time + 1);
    }
    // geofences
    case actions.TOGGLE_VISIBILITY_GEOFENCE: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;

      return state.updateIn(['geofences', 'hidden'], (hiddenGeofences) => {
        const index = hiddenGeofences.indexOf(id);

        if (index === -1) {
          return hiddenGeofences.push(id);
        }

        return hiddenGeofences.delete(index);
      });
    }
    case actions.SET_EDITABLE_GEOFENCE: {
      REDUX_EVENT_HANDLER(type, payload);

      const geofence = payload;

      return state.setIn(['geofences', 'editable'], geofence);
    }
    case actions.UPDATE_EDITABLE_GEOFENCE: {
      REDUX_EVENT_HANDLER(type, payload);

      const { property, value } = payload;

      return state.setIn(['geofences', 'editable', property], fromJS(value));
    }
    case actions.CHANGE_GEOFENCES_SEARCH: {
      REDUX_EVENT_HANDLER(type, payload);

      const text = payload;

      return state.setIn(['geofences', 'search'], text);
    }
    case actions.CHANGE_GEOFENCES_HOVER: {
      REDUX_EVENT_HANDLER(type, payload);

      const id = payload;

      return state.setIn(['geofences', 'hoverId'], id);
    }
    case geofencesActions.GEOFENCES_FETCH_FAILURE: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['geofences', 'fetchError'], true);
    }
    // simulations
    case actions.CHANGE_SIMULATIONS_ORDERING: {
      REDUX_EVENT_HANDLER(type, payload);

      const newId = payload;

      const id = state.getIn(['simulations', 'ordering', 'id']);

      if (id === newId) {
        return state.updateIn(
          ['simulations', 'ordering', 'sort'],
          sortOrdering => !sortOrdering
        );
      }

      return state.setIn(
        ['simulations', 'ordering'],
        Map({
          id: newId,
          sort: false
        })
      );
    }
    case actions.CHANGE_SIMULATIONS_SEARCH: {
      REDUX_EVENT_HANDLER(type, payload);

      const text = payload;

      return state.setIn(['simulations', 'search'], text);
    }
    case actions.TOGGLE_SELECTED_SIMULATION: {
      REDUX_EVENT_HANDLER(type, payload);

      const simulation = payload;
      return state.updateIn(['simulations', 'selected'], (selected) => {
        const index = selected.indexOf(simulation);
        if (index === -1) {
          return selected.push(simulation);
        }

        return selected.delete(index);
      });
    }
    case actions.CLEAR_SELECTED_SIMULATIONS: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['simulations', 'selected'], List());
    }
    case actions.TOGGLE_COMPARE_MOD_SIMULATIONS: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.updateIn(
        ['simulations', 'compareMode'],
        compareMode => !compareMode
      );
    }
    // modalWindow
    case actions.OPEN_MODAL_WINDOW: {
      REDUX_EVENT_HANDLER(type, payload);

      return state
        .set('modalWindow', payload)
        .setIn(['newDataset', 'error'], null)
        .setIn(['newDataset', 'status'], null)
        .setIn(['newDataset', 'progress'], 0)
        .setIn(['importBookings', 'error'], null)
        .setIn(['importBookings', 'status'], null)
        .setIn(['importBookings', 'progress'], 0)
        .setIn(['progressWindow', 'status'], 'Preparing')
        .setIn(['progressWindow', 'progress'], 1);
    }
    case actions.CLOSE_MODAL_WINDOW: {
      REDUX_EVENT_HANDLER(type, payload);

      return state
        .set('modalWindow', null)
        .setIn(['newDataset', 'error'], null)
        .setIn(['newDataset', 'status'], null)
        .setIn(['newDataset', 'progress'], 0)
        .setIn(['importBookings', 'error'], null)
        .setIn(['importBookings', 'status'], null)
        .setIn(['importBookings', 'progress'], 0)
        .setIn(['progressWindow', 'status'], null)
        .setIn(['progressWindow', 'progress'], 0);
    }
    // newDataset
    case datasetsActions.UPLOAD_DATASET_PROGRESS: {
      REDUX_EVENT_HANDLER(type, payload);

      const progress = payload;
      return state.setIn(['newDataset', 'progress'], progress);
    }
    case datasetsActions.UPLOAD_DATASET_REQUEST: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['newDataset', 'progress'], 0.01);
    }
    case datasetsActions.UPLOAD_DATASET_SUCCESS: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['newDataset', 'status'], 'success');
    }
    case datasetsActions.UPLOAD_DATASET_FAILURE: {
      REDUX_EVENT_HANDLER(type, payload);

      const error = payload;
      return state
        .setIn(['newDataset', 'error'], error.message)
        .setIn(['newDataset', 'status'], null)
        .setIn(['newDataset', 'progress'], 0);
    }
    // Login
    case userActions.AUTHORIZATION_REQUEST: {
      REDUX_EVENT_HANDLER(type, payload);

      return state
        .setIn(['login', 'error'], null)
        .setIn(['login', 'waiting'], true);
    }
    case userActions.AUTHORIZATION_SUCCESS: {
      REDUX_EVENT_HANDLER(type, payload);

      return state.setIn(['login', 'waiting'], false);
    }
    case userActions.AUTHORIZATION_FAILURE: {
      REDUX_EVENT_HANDLER(type, payload);

      const error = payload;
      return state
        .setIn(['login', 'waiting'], false)
        .setIn(['login', 'error'], error.message);
    }
    case commuteOfferActions.COMMUTE_OFFER_IMPORT_BOOKINGS_REQUEST: {
      REDUX_EVENT_HANDLER(type, payload);

      return state
        .setIn(['importBookings', 'error'], null)
        .setIn(['importBookings', 'status'], 'Processing')
        .setIn(['importBookings', 'progress'], 0.01);
    }
    case commuteOfferActions.COMMUTE_OFFER_IMPORT_BOOKINGS_PROGRESS: {
      REDUX_EVENT_HANDLER(type, payload);

      const { status, progress } = payload;

      return state
        .setIn(['importBookings', 'error'], null)
        .setIn(['importBookings', 'status'], status)
        .setIn(['importBookings', 'progress'], progress);
    }
    case commuteOfferActions.COMMUTE_OFFER_IMPORT_BOOKINGS_SUCCESS: {
      REDUX_EVENT_HANDLER(type, payload);

      return state
        .setIn(['importBookings', 'error'], null)
        .setIn(['importBookings', 'status'], null)
        .setIn(['importBookings', 'progress'], 0)
        .setIn(['commuteOffer', 'isChanged'], true)
        .set('modalWindow', null);
    }
    case commuteOfferActions.COMMUTE_OFFER_IMPORT_BOOKINGS_FAILURE: {
      REDUX_EVENT_HANDLER(type, payload);

      const error = payload;

      return state
        .setIn(['importBookings', 'error'], error.message)
        .setIn(['importBookings', 'status'], null)
        .setIn(['importBookings', 'progress'], 0);
    }
    default:
      return state;
  }
};

export default reducer;
